import { usePaneRouterContextSafe } from "@/components/PaneRouter";
import { ROUTES } from "@/constants/routes";
import { useAppNavigate, useMainRouteParams } from "@/hooks/pane-routing";
import { route } from "@/utils/route";
import type { FileRecord } from "@cohesive/api-client";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Opens `FileRecordDetailPane` for a given FileRecord id in the right pane.
 *
 * Two-hop navigation when the caller is *inside* a pane context (e.g. a chat
 * bubble rendered by a `SessionPane` that itself lives in `rightPane`).
 */
export function useOpenFileRecord() {
  const appNavigate = useAppNavigate();
  const navigate = useNavigate();
  const paneContext = usePaneRouterContextSafe();
  const { canvasId } = useMainRouteParams();

  return useCallback(
    (fileRecordId: string, flowSessionId?: string) => {
      if (!fileRecordId) return;

      const detailPath = route(ROUTES.FILE_RECORDS.DETAIL, { fileRecordId });

      // Standalone chat (inside a pane, no canvas, has session id):
      // Two-hop so the chat slides into main and the file opens in the right pane.
      if (paneContext && !canvasId && flowSessionId) {
        navigate(route(ROUTES.SESSIONS.SESSION, { sessionId: flowSessionId }), {
          state: {
            rightPane: { pathname: detailPath },
          },
        });
        return;
      }

      appNavigate({ pane: "rightPane", to: detailPath });
    },
    [appNavigate, navigate, paneContext, canvasId],
  );
}

/**
 * Opens `FileRecordDetailPane` with a pre-resolved `FileRecord` passed via
 * route state — used for legacy `base: "image" | "video"` entities that have
 * been adapted inline and don't have a real file_record id in the database.
 */
export function useOpenLegacyEntityAsFileRecord() {
  const appNavigate = useAppNavigate();
  const navigate = useNavigate();
  const paneContext = usePaneRouterContextSafe();
  const { canvasId } = useMainRouteParams();

  return useCallback(
    (record: FileRecord, flowSessionId?: string) => {
      const paneState = { fileRecord: record };
      const detailPath = route(ROUTES.FILE_RECORDS.DETAIL, {
        fileRecordId: record.id,
      });

      if (paneContext && !canvasId && flowSessionId) {
        navigate(route(ROUTES.SESSIONS.SESSION, { sessionId: flowSessionId }), {
          state: {
            rightPane: { pathname: detailPath, state: paneState },
          },
        });
        return;
      }

      appNavigate({
        pane: "rightPane",
        to: detailPath,
        options: { state: paneState },
      });
    },
    [appNavigate, navigate, paneContext, canvasId],
  );
}
